import { Injectable, computed, inject } from '@angular/core';
import { TenantSettingsService } from './tenant-settings.service';
import { TenantTimeZoneService } from './tenant-timezone.service';
import { SubscriptionPaymentStatus } from '../constants';
import { BillingSettlement, PLAN_ENTITLEMENT_MESSAGES, PLAN_PAYMENT_MESSAGES } from './plan-entitlement-constants';

// =============================================================================
// PlanEntitlementService (admin portal)
// Typed, signal-based view over the TENANT'S PLAN — what the association bought.
//
// ── Not the same thing as FeatureFlagService ──────────────────────────────────
// FeatureFlagService reads ENVIRONMENT flags: per-deployment switches that say
// "this build of Membora doesn't offer X here" (e.g. the demo environment).
// This service reads PLAN entitlements: per-tenant, "your subscription doesn't
// include X".
//
// Storefront and SSO exist in BOTH. They must never be collapsed into a single
// boolean, because the cause and the remedy differ — an environment flag is not
// something the customer can buy their way out of, and a plan limit is not
// something support can toggle. Both feed <ams-feature-lock>, which is pure
// presentation and knows about neither.
//
// Phase A carries only the degraded signal; the plan's own feature list joins it
// when module gating lands.
// =============================================================================
@Injectable({ providedIn: 'root' })
export class PlanEntitlementService {
  private readonly tenantSettings = inject(TenantSettingsService);

  /**
   * True when plan limits are being served from the last-known-good snapshot
   * because SaaSLogic could not be reached.
   *
   * Defaults to FALSE when the payload is absent — an older API build or
   * settings not yet loaded is not evidence of an outage, and a banner that
   * appears on every cold start would train people to ignore it.
   */
  readonly isDegraded = computed(
    () => this.tenantSettings.entitlements()?.isDegraded ?? false,
  );

  /** Explanatory text shown while {@link isDegraded} is true. */
  readonly degradedMessage = computed(() => PLAN_ENTITLEMENT_MESSAGES.degraded);

  private readonly tenantTimeZone = inject(TenantTimeZoneService);

  /**
   * The billing provider's settlement state, or null when there is nothing to say
   * (free tier, or degraded — the API withholds it rather than serve a stale figure).
   */
  private readonly paymentStatus = computed(
    () => this.tenantSettings.entitlements()?.paymentStatus ?? null,
  );

  /** An invoice is issued and awaiting payment, not yet late. */
  readonly isPaymentDue = computed(
    () => this.paymentStatus() === SubscriptionPaymentStatus.PaymentDue,
  );

  /** Payment is late — access ends at the provider's grace deadline. */
  readonly isPastDue = computed(
    () => this.paymentStatus() === SubscriptionPaymentStatus.PastDue,
  );

  /**
   * The settlement axis on its own, for {@link billingStatusFor}.
   *
   * Falls to 'settled' when nothing is reported — free tier, or a degraded payload where
   * the API withholds paymentStatus deliberately. Absence of evidence is not a debt.
   */
  readonly settlement = computed<BillingSettlement>(() =>
    this.isPastDue() ? 'pastDue' : this.isPaymentDue() ? 'due' : 'settled',
  );

  /** True when either payment state warrants a notice. */
  readonly hasPaymentNotice = computed(() => this.isPastDue() || this.isPaymentDue());

  /**
   * Past-due reads as an error, invoice-due as information. Not two shades of
   * warning: one has a deadline attached and the other is a receipt.
   */
  readonly paymentNoticeVariant = computed<'error' | 'info'>(
    () => (this.isPastDue() ? 'error' : 'info'),
  );

  /**
   * The notice text, with dates rendered through `formatDateOnly` — which applies no
   * timezone by contract. These values are bare calendar dates; running them through
   * a timezone-aware formatter is what makes a due date land on the wrong day for a
   * reader in another zone.
   */
  readonly paymentNoticeMessage = computed(() => {
    const entitlements = this.tenantSettings.entitlements();

    if (this.isPastDue()) {
      return PLAN_PAYMENT_MESSAGES.pastDue(
        this.tenantTimeZone.formatDateOnly(entitlements?.accessEndsOn, 'long'),
      );
    }

    return PLAN_PAYMENT_MESSAGES.due(
      this.tenantTimeZone.formatDateOnly(entitlements?.paymentDueOn, 'long'),
    );
  });
}
